<template>
  <li>
    <div class="favorites-card">
      <div class="favorites-card-head">
        <div class="company">
          <div class="company-logo">
            <img class="w-100 h-100" :src="employerLogo" :alt="item.company" />
          </div>
          <div class="company-name">
            <NuxtLink
              :to="{
                name: 'vacancies-slug',
                params: { slug: item.provider_vacancy_id || item.id },
                query: { provider: 'hh' },
              }"
            >
              {{ item.name }}
            </NuxtLink>
            <span class="count">{{ item.company }}</span>
          </div>
        </div>
        <div class="salary">{{ salaryText }}</div>
      </div>
      <div class="favorites-card-body">
        <div class="time-location">
          <span>{{ moment(item.published_date).format("hh:mm") }}</span>
          <strong>{{ item.city }}</strong>
        </div>
        <p>{{ vacancyDescription }}</p>
      </div>
      <div class="favorites-card-footer">
        <div class="favorites-card-footer-row">
          <div class="group me-auto">
            <div class="select-resume-row" v-if="isAuthenticated">
              <div class="custom-select-wrapper">
                <CustomSelectWithRadio
                  label="Выберите резюме"
                  v-model="selectedResume"
                  :options="myResumeOptions"
                />
                <button
                  id="apply-button"
                  class="btn apply-button button-accent"
                  :disabled="!selectedResume"
                  @click.prevent="onSubmit"
                >
                  Откликнуться
                </button>
              </div>
              <span class="text text-danger" v-if="selectedResumeError">
                {{ selectedResumeError }}
              </span>
            </div>
            <div
              v-else
              class="select-resume-row d-inline-flex justify-content-center align-items-center"
            >
              <NuxtLink class="button-accent" :to="{ name: 'sign-in' }"
                >Войти</NuxtLink
              >
              чтобы откликаться
            </div>
          </div>
          <div class="group">
            <button
              class="group-action ic-btn fav-btn"
              :class="{ active: isFavorite }"
              @click="toggleFavorite"
            >
              <svg
                width="23"
                height="21"
                viewBox="0 0 23 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.0641 0.796788C11.2552 0.45665 11.7448 0.456649 11.9359 0.796789L14.7993 5.89364C15.0135 6.27499 15.3835 6.54383 15.8124 6.62973L21.5446 7.77794C21.9272 7.85457 22.0785 8.32027 21.8141 8.60711L17.8515 12.9053C17.555 13.2269 17.4137 13.6619 17.4645 14.0964L18.1439 19.9029C18.1892 20.2903 17.793 20.5782 17.4385 20.4153L12.1262 17.9749C11.7287 17.7923 11.2713 17.7923 10.8738 17.9749L5.56148 20.4153C5.20696 20.5782 4.81081 20.2903 4.85614 19.9029L5.53549 14.0964C5.58632 13.6619 5.44498 13.2269 5.1485 12.9053L1.18593 8.60711C0.921493 8.32027 1.07281 7.85457 1.45535 7.77794L7.18757 6.62973C7.61645 6.54383 7.98648 6.27499 8.20072 5.89364L11.0641 0.796788Z"
                  stroke="#C8C8C8"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script async setup>
// Import core libraries and dependencies
import moment from "moment";
import { useVacancyStore } from "~/store/vacancy";
import Swal from "sweetalert2";
import { toast } from "vue3-toastify";
import { ref, computed } from "vue";
import { useResumeStore } from "~/store/resume.js";
import { useAuthStore } from "~/store/auth.js";

// Use the global formatting function from Nuxt context
const { $format_number } = useNuxtApp();

// Receive input property for the component
const props = defineProps(["item"]);
const { item } = props;

// Compute the formatted salary text to be displayed
const salaryText = computed(() => {
  if (item.salary_from && item.salary_to) {
    return `${$format_number(item.salary_from)} - ${$format_number(
      item.salary_to,
    )} ${item.currency}`;
  } else if (item.salary_from) {
    return `От ${$format_number(item.salary_from)} ${item.currency}`;
  } else if (item.salary_to) {
    return `До ${$format_number(item.salary_to)} ${item.currency}`;
  }
  return "По договору"; // Default text if no salary is defined
});

// Track whether the vacancy is marked as a favorite
const isFavorite = ref(item.is_favorite ?? false);

// Reference for storing the selected resume
const selectedResume = ref(null);

// Access the resume store and the current authentication state
const resumeStore = useResumeStore();
const isAuthenticated = computed(() => useAuthStore().isAuthed);

// Generate resume options to be displayed in the dropdown
const myResumeOptions = computed(() =>
  resumeStore.my_resumes.map((res) => ({
    name: res.title,
    value: res.id,
  })),
);

// Generate a concise description for the vacancy
const vacancyDescription = computed(() => {
  if (item.description) {
    let text = item.description.slice(0, 150);
    if (item.description.length > 150) {
      text += "...";
    }
    return text;
  }
  return item.description;
});

// Manage the error message for resume selection
const selectedResumeError = ref(null);

// Handle the submission of a resume when applying for a vacancy
const onSubmit = async () => {
  if (!selectedResume.value) {
    selectedResumeError.value = "Выберите резюме чтобы откликатся.";
    return;
  }

  try {
    const response = await resumeStore.submitResume({
      vacancy_id: item.id,
      resume_id: selectedResume.value,
      providers: ["hh"],
    });

    if (response.status === "success") {
      toast.success("Вы успешно откликнулись на вакансию.");
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    await Swal.fire({
      title: "Ошибка!",
      text: error.message,
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};

// Toggle the favorite status of the vacancy
const vacancyStore = useVacancyStore();
const toggleFavorite = async () => {
  try {
    if (isFavorite.value) {
      await vacancyStore.removeFromFavorite(item.favorite_id);
    } else {
      await vacancyStore.addToFavorite({
        id: String(item.id),
        provider: item.provider,
      });
    }
    isFavorite.value = !isFavorite.value;
  } catch (error) {
    await Swal.fire({
      title: "Ошибка!",
      text: error.message,
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};

// Get the employer logo or default to a placeholder
const employerLogo = computed(() =>
  item.logo
    ? item.logo
    : new URL("/assets/img/logos/superjob.svg", import.meta.url),
);
</script>

<style scoped></style>
